/** @format */

import React, { memo, MouseEvent, useState } from 'react';

import { Link } from 'gatsby-plugin-intl';
import styled from 'styled-components';
import { Swiper, SwiperSlide } from 'swiper/react';

import { selectorBookingCommercialConditions } from '../../../../@ducks/booking/selectors';
import { useGallery } from '../../../../@ducks/general/hooks/useGallery';
import { selectorCurrentPage } from '../../../../@ducks/general/selectors';
import { GTMvirtualTour, utilityGtmGalleryImage } from '../../../../@ducks/googleTagManager/helpers/google-tag-manager';
import { TRoom } from '../../../../@ducks/room/declarations';
import { utilityGetRoomPictures } from '../../../../@ducks/search/helpers/getRoomPictures';
import ZoomIcon from '../../../../assets/icons/dovevivo_icon_zoom.svg';
import VrIcon from '../../../../assets/play-button.svg';
import { useAppTranslation } from '../../../../i18n/declarations';
import { useAppSelector } from '../../../../redux/hooks';
import { mediaQuery } from '../../../../theme/mediaQuery';
import { utilityGetPrice } from '../../../../utils/helpers/utilityGetPrice';
import useModal from '../../Modal';

interface Props {
  media: Array<any>;
  hasInactiveURL: boolean;
  hasPromo: boolean;
  roomURL: string;
  room: TRoom;
}

export const Pictures = memo(({ media, hasInactiveURL, hasPromo, roomURL, room }: Props) => {
  const [cardSwiper, setCardSwiper] = useState<any>();
  const [cardIndex, setCardIndex] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const { t } = useAppTranslation();
  const currentPage = useAppSelector(selectorCurrentPage);
  const mediaFormatted = media.filter(photo => photo.label !== 'Planimetry');
  const { photosLowQuality, photosHighQuality, urlVR, hasVR, hasMultiplePhotos } = utilityGetRoomPictures({
    media: mediaFormatted,
  });
  const commercialConditions = useAppSelector(selectorBookingCommercialConditions);
  const { getFormattedPrice } = utilityGetPrice({ room, commercialConditions });

  const { Modal: ModalVR } = useModal();
  const { Gallery: ModalGallery } = useGallery();

  const defaultRoomImage =
    'https://cdn.dovevivo.it/images/Photos/default_rappresentative/single_rooms/09MQ/small_bed/A/_MG_0487.jpg';

  const onSetCardIndex = s => {
    if (s.activeIndex !== cardIndex && s.activeIndex !== 0) {
      setCardIndex(s.realIndex + 1);
      utilityGtmGalleryImage(s.realIndex + 1, room.code, getFormattedPrice());
    }
  };

  const renderPhoto = (urlImage: string, index: number) => {
    const srcImage = index < cardIndex ? urlImage : '';

    return (
      <SwiperSlide key={`room-picture-${index}`}>
        <Photo width={480} height={320} src={srcImage} alt={`${t('listing_alt_img')}${index + 1} - ${room.code}`} />
      </SwiperSlide>
    );
  };

  const NotAvailablePicture = () => (
    <ContainerNotImage>
      <Text>{t('picture_not_available')}</Text>
      <Photo
        width={480}
        height={320}
        style={{ opacity: '0.3' }}
        src={defaultRoomImage}
        alt={t('picture_not_available')}
      />
    </ContainerNotImage>
  );

  const handleClickIconVr = (event: MouseEvent<HTMLButtonElement>) => {
    setShowModal(true);
    event.stopPropagation();

    if (currentPage === 'city-page' || currentPage === 'neighborhood-page') {
      const gtmNameNeighborhood = `${room.city.name}_${room.district.name.split('/')[0].trimEnd()}`;
      const gtmName = currentPage === 'city-page' ? `city_page_${room.city.name}` : gtmNameNeighborhood.toLowerCase();
      GTMvirtualTour({ eventLabel: gtmName, moreInfo: room.code, monthlyRate: getFormattedPrice() });
    } else if (currentPage === 'listing') {
      GTMvirtualTour({ eventLabel: 'listing_solution', moreInfo: room.code, monthlyRate: getFormattedPrice() });
    } else {
      GTMvirtualTour({ eventLabel: 'room_detail_page', moreInfo: room.code, monthlyRate: getFormattedPrice() });
    }
  };

  return (
    <>
      {showGallery && (
        <ModalGallery
          isShow={showGallery}
          pictures={photosHighQuality}
          index={cardIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
      {hasVR && (
        <ModalVR isShow={showModal} onClose={() => setShowModal(false)}>
          <Container>
            <Frame src={urlVR} frameBorder="0" />
            <ButtonVR onClick={() => setShowModal(false)}>&times;</ButtonVR>
          </Container>
        </ModalVR>
      )}
      <div style={{ position: 'relative' }}>
        {hasInactiveURL ? (
          <>
            {hasMultiplePhotos ? (
              <Swiper
                preloadImages={false}
                lazy
                onSwiper={swiper => setCardSwiper(swiper)}
                controller={{ control: cardSwiper }}
                pagination={{ clickable: true, dynamicBullets: true }}
                loop
                onActiveIndexChange={onSetCardIndex}
              >
                {photosHighQuality ? photosHighQuality.map(renderPhoto) : <NotAvailablePicture />}
              </Swiper>
            ) : (
              <>
                {photosHighQuality ? (
                  <Photo width={480} height={320} src={photosHighQuality[0]} alt={t('gallery_label_Rappresentative')} />
                ) : (
                  <NotAvailablePicture />
                )}
              </>
            )}
          </>
        ) : (
          <Link to={roomURL}>
            {hasMultiplePhotos ? (
              <Swiper
                preloadImages={false}
                lazy
                onSwiper={swiper => setCardSwiper(swiper)}
                controller={{ control: cardSwiper }}
                pagination={{ clickable: true, dynamicBullets: true }}
                loop
                onActiveIndexChange={onSetCardIndex}
              >
                {photosLowQuality ? photosLowQuality.map(renderPhoto) : <NotAvailablePicture />}
              </Swiper>
            ) : (
              <>
                {photosLowQuality ? (
                  <Photo width={480} height={320} src={photosLowQuality[0]} alt={t('gallery_label_Rappresentative')} />
                ) : (
                  <NotAvailablePicture />
                )}
              </>
            )}
          </Link>
        )}
        {hasMultiplePhotos && (
          <IconZoom onClick={() => setShowGallery(true)} aria-hidden>
            <img src={ZoomIcon} alt="zoom-icon" />
          </IconZoom>
        )}
        {hasVR && (
          <IconVR onClick={handleClickIconVr} promo={hasPromo}>
            <img src={VrIcon} alt="virtual-tour" />
            <TooltipVR>Virtual tour</TooltipVR>
          </IconVR>
        )}
      </div>
    </>
  );
});

// #region ::: COMPONENTS

const ContainerNotImage = styled.div`
  background: #0000004a;
`;

const Text = styled.p`
  position: absolute;
  top: 40%;
  left: 25%;
  color: ${({ theme }) => theme.colors.joivy.black};
  background: #00000021;
  z-index: 2;
`;

const Photo = styled.img`
  height: 320px;
  background-size: cover;
`;

const IconZoom = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: 2;
  height: 45px;
  width: 45px;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const IconVR = styled.button<{ promo?: boolean }>`
  background-color: ${({ theme }) => theme.colors.joivy.pink};
  border: 0;
  border-radius: 100px;
  cursor: pointer;
  font-size: 0;
  padding: 8px 6px 10px 10px;
  position: absolute;
  right: 20px;
  top: ${({ promo }) => (promo ? '45px' : '17px')};
  z-index: 1;
  width: 40px;
  height: 40px;

  transition: all 0.3s ease-in-out;

  ${mediaQuery.sm`
    width: 30px;
    height: 30px;
  `}

  &:hover {
    background-color: ${({ theme }) => theme.colors.joivy['pink-light']};
  }
`;

const TooltipVR = styled.div`
  display: none;
  position: absolute;
  background-color: ${({ theme }) => theme.colors.joivy.white};
  color: ${({ theme }) => theme.colors.joivy.black};
  font-size: 12px;
  line-height: 1.4;
  right: 10%;
  bottom: 1px;
  transform: translateX(-50%);
  white-space: nowrap;
  padding: 5px 7px;
  border-radius: 5px;

  ${IconVR}:hover & {
    display: block;
  }
`;

const ButtonVR = styled.button`
  padding: 0;
  position: absolute;
  top: 20px;
  right: 15px;
  line-height: 0;
  font-size: 40px;
  border: 0;
  color: #${({ theme }) => theme.colors.joivy.white};
  cursor: pointer;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const Frame = styled.iframe`
  height: 80%;
  left: 10%;
  top: 10%;
  width: 80%;
  position: absolute;
`;
// #endregion
